import { canOpenAdminApp, isVenueRole, type Me } from '@otryad/shared';
import { Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router';

import { logout } from '@/lib/api';
import { texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

export interface AdminOutletContext {
  me: Me;
  signOut: () => Promise<void>;
}

export function useAdmin(): AdminOutletContext {
  return useOutletContext<AdminOutletContext>();
}

/**
 * Guard по cookie-сессии и роли: `auth.me` → нет сессии → `/login`;
 * есть сессия, но роль не ops/admin → экран 403 (роли проверяет сервер, здесь только отображение).
 */
export function AuthGuard() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const me = trpc.auth.me.useQuery(undefined, { retry: false });

  const signOut = async () => {
    await logout();
    queryClient.clear();
    window.location.assign('/login');
  };

  if (me.isPending) {
    return <CenterMessage title={texts.auth.checking} />;
  }

  if (me.isError) {
    if (me.error.data?.code === 'UNAUTHORIZED') return <Navigate to="/login" replace />;
    return (
      <CenterMessage title={texts.common.error} body={me.error.message}>
        <button
          type="button"
          onClick={() => void me.refetch()}
          className="text-accent text-sm font-semibold"
        >
          {texts.common.retry}
        </button>
      </CenterMessage>
    );
  }

  if (!canOpenAdminApp(me.data.user.role)) {
    return (
      <CenterMessage title={texts.auth.forbiddenTitle} body={texts.auth.forbiddenBody}>
        <p className="text-sm text-muted">
          {me.data.user.firstName} · {texts.auth.role}: {texts.role[me.data.user.role]}
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-accent text-sm font-semibold"
        >
          {texts.auth.logout}
        </button>
      </CenterMessage>
    );
  }

  // Роль VENUE живёт только в портале площадки, разделы админки ей закрыты (Stage 14)
  const venue = isVenueRole(me.data.user.role);
  // Именно портал, а не раздел площадок `/venues` — префикс без границы съедал бы его
  const inPortal = location.pathname === '/venue' || location.pathname.startsWith('/venue/');
  if (venue && !inPortal) return <Navigate to="/venue" replace />;
  if (!venue && inPortal) return <Navigate to="/pool" replace />;

  const ctx: AdminOutletContext = { me: me.data.user, signOut };
  // Suspense ленивых страниц живёт здесь, а не отдельным маршрутом-обёрткой: любой промежуточный
  // `<Outlet />` без `context` подставляет провайдер со значением undefined и обнуляет контекст
  // для всего, что ниже (Stage 16, раунд 1 — на этом падала вся админка).
  return (
    <Suspense fallback={<div className="text-muted p-6 text-sm">{texts.common.loading}</div>}>
      <Outlet context={ctx} />
    </Suspense>
  );
}

function CenterMessage({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children?: React.ReactNode;
}) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-xl font-bold tracking-tight">{title}</h1>
      {body ? <p className="max-w-md text-sm text-muted">{body}</p> : null}
      {children}
    </main>
  );
}
